import type { FC } from "react";

import type {
    DefaultValueGeneratorMapType,
    DefaultValueValidation,
} from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import { DisplayText } from "../../../../../components/EditableList/components/ListComponents/DisplayText";
import { Checkbox, TextInput } from "../../../../../components/Form/formComponents";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import type { AntCustomFormField } from "../../../../../components/Form/types/types";
import { FormComponent } from "../../../../../components/Form/types/types";
import { deepClone } from "../../../../../core/utils/utilFunctions";

export interface ApplicationConfigurationsDefaultValueEditProps extends AntCustomFormField<DefaultValueValidation> {}

export const ApplicationConfigurationsDefaultValueEdit: FC<ApplicationConfigurationsDefaultValueEditProps> = ({ onChange, value }) => {
    const defaultValue = value?.value || "";
    const componentType = value?.componentType;

    const onDefaultValueChange = (changeValue: string | boolean) => {
        const newValue = deepClone(value);
        newValue.value = changeValue;
        if (onChange) {
            onChange(newValue);
        }
    };

    // TODO refactor code and define behaviour for TBC components AP3ADMIN-2252
    const NO_DEFAULT_VALUE = "Default value cannot be configured for this field component";
    const DEFAULT_VALUE_TBC = "Default value cannot be configured for this field component";

    const defaultValueGenerator: Record<FormComponent, DefaultValueGeneratorMapType> = {
        [FormComponent.AUTOCOMPLETE]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.TEXT]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.TEXT_AREA]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.TEXT_TEMPLATING]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.INPUT_GENERATOR]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.SITE_ID_GENERATOR]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.SITE_ID_COPY_GENERATOR]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.TERMINAL_ID_GENERATOR]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.TERMINAL_ID_COPY_GENERATOR]: (
            <TextInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.NUMBER]: (
            <NumberInput label="" name="" readOnly={false} value={defaultValue.toString()} onChange={onDefaultValueChange} />
        ),
        [FormComponent.CHECK_BOX]: <Checkbox label="" name="" readOnly={false} value={!!defaultValue} onChange={onDefaultValueChange} />,
        [FormComponent.HIDDEN_INPUT]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.DISPLAY_TEXT]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.HIDDEN_CHECK_BOX]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.APPLICATION_CONFIGURATIONS_VALIDATIONS_EDIT]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.APPLICATION_CONFIGURATIONS_DEFAULT_VALUE_EDIT]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.ACCEPT_TYPE_SELECTOR]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.READ_ONLY_CHECK]: <DisplayText value={NO_DEFAULT_VALUE} />,
        [FormComponent.SELECT_DYNAMIC]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.MULTI_SELECT_DYNAMIC]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.DYNAMIC_PROPERTIES]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.UPLOAD_FILE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.PROFILE_SELECTOR]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.TIME_PICKER]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.DATE_PICKER]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.FILE_CONTENTS]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.SITES_TABLE_VIEW]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.DATA_GROUP_USERS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.DATA_GROUP_IDM_USERS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.DYNAMIC_PROPERTY_GROUP_PROPERTIES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CONTRACTS_TERMINALS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CONTRACTS_APP_PROFILES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CONTRACTS_VIRTUAL_APP_PROFILES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.VIRTUAL_ACQUIRERS_PHYSICAL_ACQUIRERS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.ACQUIRERS_OPERATORS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.SITES_CONTRACTS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.APPLICATIONS_PROFILE_PROMPT_DATA]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.APPLICATIONS_PROFILE_CARD_DEFINITIONS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.PARAMETERS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.TERMINAL_CONTRACTS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.TERMINAL_HOST_TERMINAL]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CARD_DEFINITIONS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CARD_ATTRIBUTES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.ACQUIRERS_CONTRACTS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.EMV_KERNEL_EMV_CONFIGURATIONS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.EMV_KERNEL_CA_PUBLIC_KEYS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.EMV_KERNEL_AID_PREFERENCES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.EMV_KERNEL_AID_PREFERENCES_CLESS_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CAMPAIGN_TERMINALS_SELECTABLE_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.TERMINAL_TO_CAMPAIGN_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.VAT_CODE_VAT_VALUES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CARD_COMBINATIONS]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CARD_TYPES_MAPPING_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.VIRTUAL_SITES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.PRODUCT_PROFILE_PRODUCTS]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.CAMPAIGN_TERMINALS_VIEW_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.TERMINALS_TEXTAREA]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.SITES_VIRTUAL_SITES_TABLE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        [FormComponent.ORG_UNITS_PRODUCTS_PROFILE]: <DisplayText value={DEFAULT_VALUE_TBC} />,
        "": null,
    };

    const defaultValueInputComponentGenerator = (componentInput: FormComponent) => {
        return defaultValueGenerator[componentInput];
    };

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{componentType && defaultValueInputComponentGenerator(componentType)}</>;
};
